import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, NumberField, SimpleForm, TextInput, SelectInput, NumberInput, Button, Filter, SearchInput, useGetList, useUpdate, useNotify, useRefresh, useRecordContext, ReferenceInput } from 'react-admin';
import { OfoCodeAutocomplete } from '@/components/ofo/OfoCodeAutocomplete';
import { useEmployeeMap, getEmployeeName, getEmployeeField } from '@/hooks/useEmployeeMap';
import { useState } from 'react';

import { Box, Typography, Paper, Chip, LinearProgress, Alert, Grid, Divider, Avatar, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, TablePagination, Stepper, Step, StepLabel, Card, CardContent, CircularProgress, Tooltip, IconButton } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { School, CheckCircle, Schedule, Cancel, TrendingUp, Person, Work, Business, Receipt, Assignment, HourglassEmpty, TrendingDown, Info, Close } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { ProcessActions, ProcessActionsGrouped } from '@/components/workflow/ProcessActions';

const INTERVENTION_TYPES = ['Internal', 'External', 'Bursary', 'Learnership', 'Apprenticeship', 'Mentorship'];
const TRAINING_STATUS = ['Planned', 'InProgress', 'Completed', 'NotCompleted', 'Cancelled'];

const getCostAmount = (cost: unknown): number => {
  if (!cost) return 0;
  if (typeof cost === 'number') return cost;
  if (typeof cost === 'object' && (cost as Record<string, unknown>).amount != null) return Number((cost as Record<string, unknown>).amount);
  return 0;
};

const formatCurrency = (amount: number): string => `R ${amount.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

interface TrainingRecord {
  id?: string;
  status?: string;
  courseName?: string;
  provider?: string;
  startDate?: string;
  endDate?: string;
  cost?: unknown;
  employeeId?: string;
  employeeName?: string;
  employee?: { id?: string };
  interventionType?: string;
  ofoCode?: string;
  skillsProgramme?: string;
  nqfLevel?: string;
  credits?: number;
  competency?: string;
  completionDate?: string;
  certificateNumber?: string;
  fundingSource?: string;
  totalCost?: unknown;
  setaReported?: string;
  [key: string]: unknown;
}

const STATUS_COLORS: Record<string, string> = {
  Planned: 'default',
  InProgress: 'info',
  Completed: 'success',
  NotCompleted: 'warning',
  Cancelled: 'error',
};
const INTERVENTION_COLOR_MAP: Record<string, string> = {
  'Internal': '#1976d2',
  'External': '#ed6c02',
  'Bursary': '#2e7d32',
  'Learnership': '#9c27b0',
  'Apprenticeship': '#00acc1',
  'Mentorship': '#757575',
};

const TrainingSparkline = ({ startDate, endDate, status }: { startDate: string; endDate: string; status: string }) => {
  const [now] = useState(() => Date.now());
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const totalDuration = end - start;
  const elapsed = now - start;
  const remaining = end - now;
  const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));

  if (status === 'Completed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={{ height: 8, bgcolor: 'success.main', borderRadius: 1, opacity: 0.3 }} />
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'success.main', borderRadius: 1 }} />
          </Box>
        </Box>
        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'right', color: 'success.main', fontWeight: 600 }}>
          Done
        </Typography>
      </Box>
    );
  }

  if (status === 'Planned' || status === 'NotCompleted' || status === 'Cancelled') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 1 }} />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'right', color: 'text.secondary', fontWeight: 600 }}>
          {status === 'Planned' ? 'Planned' : status === 'NotCompleted' ? 'Incomplete' : 'Cancelled'}
        </Typography>
      </Box>
    );
  }

  const pctRemaining = Math.max(0, Math.min(100, (remaining / totalDuration) * 100));

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 14) return 'error';
    if (daysRemaining <= 30) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${100 - pctRemaining}%`,
              height: '100%',
              bgcolor: `${getColor()}.main`,
              borderRadius: 1,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Box sx={{ position: 'absolute', right: 0, top: -2 }}>
          <TrendingDown sx={{ fontSize: 12, color: `${getColor()}.main` }} />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right', color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Overdue' : `${daysRemaining}d`}
      </Typography>
    </Box>
  );
};

export const TrainingList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: allTrainings, isLoading } = useGetList('training-records', { pagination: { page: 1, perPage: 1000 } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const trainings = allTrainings?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allTrainings?.length ?? 0,
    completed: allTrainings?.filter((t: TrainingRecord) => t.status === 'Completed').length ?? 0,
    inProgress: allTrainings?.filter((t: TrainingRecord) => t.status === 'InProgress').length ?? 0,
    planned: allTrainings?.filter((t: TrainingRecord) => t.status === 'Planned').length ?? 0,
    notCompleted: allTrainings?.filter((t: TrainingRecord) => t.status === 'NotCompleted').length ?? 0,
    totalCost: allTrainings?.reduce((sum: number, t: TrainingRecord) => sum + getCostAmount(t.cost), 0) ?? 0,
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const now = new Date();
  const recentMonths = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return { key: `${d.getFullYear()}-${d.getMonth()}`, label: months[d.getMonth()] };
  });
  const monthlyCompletions = recentMonths.map(({ key }) => {
    const [year, month] = key.split('-').map(Number);
    return allTrainings?.filter((t: TrainingRecord) => {
      if (t.status !== 'Completed') return false;
      const date = new Date(t.startDate || t.endDate || '');
      return date.getFullYear() === year && date.getMonth() === month;
    }).length ?? 0;
  });

  const pieData = [
    { label: 'Completed', value: stats.completed },
    { label: 'In Progress', value: stats.inProgress },
    { label: 'Planned', value: stats.planned },
    { label: 'Not Completed', value: stats.notCompleted },
  ].filter(d => d.value > 0);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.skills-development" wizardPath="wizard/training" label="New Training Record" />
        </Box>
        <ListPageHeader
          title="Training Records"
          metrics={[
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'In Progress', value: String(stats.inProgress), color: 'info.main' },
            { label: 'Planned', value: String(stats.planned), color: 'default' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {trainings?.map((training: TrainingRecord) => (
            <Paper key={training.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, training.employee?.id)}</Typography>
                  <Typography variant="caption" color="text.secondary">{training.courseName}</Typography>
                </Box>
                <Chip label={training.status} size="small" color={STATUS_COLORS[training.status ?? ''] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{training.provider}</Typography>
                <Typography variant="caption" color="text.secondary">{training.startDate} → {training.endDate}</Typography>
              </Box>
              <TrainingSparkline startDate={training.startDate ?? ''} endDate={training.endDate ?? ''} status={training.status ?? ''} />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.skills-development" wizardPath="wizard/training" label="New Training Record" />
      </Box>
      <ListPageHeader
        title="Training Records"
        metrics={[
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'In Progress', value: String(stats.inProgress), color: 'info.main' },
          { label: 'Planned', value: String(stats.planned), color: 'default' },
          { label: 'Total Cost', value: formatCurrency(stats.totalCost), color: 'warning.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Training Status"
        barChartData={monthlyCompletions}
        barChartLabels={recentMonths.map(m => m.label)}
        barChartTitle="Monthly Completions"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Timeline</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trainings?.map((training: TrainingRecord) => (
              <TableRow
                key={training.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => { navigate(`/training-records/${training.id}/show`); }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: INTERVENTION_COLOR_MAP[training.interventionType ?? ''] || 'primary.main', fontSize: 12 }}>
                      {getEmployeeName(employeeMap, training.employee?.id)?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, training.employee?.id)}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{training.courseName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{training.provider}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={training.interventionType}
                    size="small"
                    sx={{ bgcolor: `${INTERVENTION_COLOR_MAP[training.interventionType ?? '']}20`, color: INTERVENTION_COLOR_MAP[training.interventionType ?? ''], fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip label={training.status} size="small" color={STATUS_COLORS[training.status ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {training.cost ? formatCurrency(getCostAmount(training.cost)) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">{training.startDate}</Typography>
                    <Typography variant="caption" color="text.secondary">→</Typography>
                    <Typography variant="caption" color="text.secondary">{training.endDate}</Typography>
                    <Box sx={{ flex: 1, ml: 1 }}>
                      <TrainingSparkline startDate={training.startDate ?? ''} endDate={training.endDate ?? ''} status={training.status ?? ''} />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allTrainings?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!trainings || trainings.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <School sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No training records found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const TrainingFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search employee or course" />
    <SelectInput source="status" choices={TRAINING_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="interventionType" choices={INTERVENTION_TYPES.map(t => ({ id: t, name: t }))} />
  </Filter>
);

const TRAINING_STAGE_DEFS = [
  { id: 'planned', label: 'Planned', icon: <Schedule fontSize="small" />, desc: 'Training scheduled and resourced' },
  { id: 'inProgress', label: 'In Progress', icon: <TrendingUp fontSize="small" />, desc: 'Training currently underway' },
  { id: 'completed', label: 'Completed', icon: <CheckCircle fontSize="small" />, desc: 'Training finished successfully' },
  { id: 'reported', label: 'SETA Reported', icon: <Assignment fontSize="small" />, desc: 'Included in WSP/ATR submission' },
];

const getStageStatus = (record: Record<string, unknown>): Record<string, boolean> => ({
  planned: true,
  inProgress: ['InProgress', 'Completed', 'NotCompleted'].includes(record.status as string),
  completed: record.status === 'Completed',
  reported: !!record.setaReported,
});

const TrainingShowContent = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const employeeMap = useEmployeeMap();
  const [now] = useState(() => Date.now());

  if (!record) return null;

    const daysSinceStart = record.startDate ? Math.ceil((now - new Date(record.startDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;
    const totalDays = record.endDate && record.startDate ? Math.ceil((new Date(record.endDate).getTime() - new Date(record.startDate).getTime()) / (1000 * 60 * 60 * 24)) : 1;
    const progress = record.status === 'Completed' ? 100 : record.status === 'InProgress' ? Math.min(100, Math.round((daysSinceStart / totalDays) * 100)) : 0;
    const progressColor = record.status === 'Completed' ? 'success' : record.status === 'InProgress' ? 'info' : 'default';
    const costPerHour = record.durationHours ? getCostAmount(record.cost) / record.durationHours : 0;
    const interventionColor = INTERVENTION_COLOR_MAP[record.interventionType] || '#1976d2';
    const TRAINING_STAGES = ['Planned', 'InProgress', 'Completed'];
    const activeStep = TRAINING_STAGES.indexOf(record.status === 'NotCompleted' ? 'InProgress' : record.status === 'Cancelled' ? 'Planned' : record.status);
    const stageStatus = getStageStatus(record);

    const handleAction = (data: Record<string, unknown>) => {
        update('training-records', { id: record.id, data }, {
            onSuccess: () => { notify('Training updated', { type: 'success' }); refresh(); },
            onError: (e: Error) => { notify(`Error: ${e.message}`, { type: 'error' }); },
        });
    };

    const handleStageToggle = (stageId: string) => {
      if (stageId === 'inProgress' && record.status === 'Planned') {
        handleAction({ status: 'InProgress', startDate: record.startDate || new Date().toISOString().split('T')[0] });
      } else if (stageId === 'completed' && record.status === 'InProgress') {
        handleAction({ status: 'Completed', endDate: record.endDate || new Date().toISOString().split('T')[0] });
      } else if (stageId === 'reported') {
        handleAction({ setaReported: !record.setaReported });
      }
    };

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={() => navigate('/training-records')} size="small" sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>
        <Paper elevation={0} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${interventionColor}, ${interventionColor}cc)`, color: 'primary.contrastText', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24 }}>
              <School sx={{ fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>{record.courseName}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip label={record.status} size="small" color={STATUS_COLORS[record.status] as ChipColor} sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 600 }} />
                <Chip label={record.interventionType} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Training ID</Typography>
              <Typography variant="h6" fontWeight={700}>{String(record.id).split(':')[1]?.substring(0, 8).toUpperCase() || 'N/A'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Course Provider</Typography>
              <Typography variant="body1" fontWeight={600}>{record.provider || '—'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>OFO Code</Typography>
              <Typography variant="body1" fontWeight={600}>{record.ofocode?.id || record.ofocCode || '—'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Duration</Typography>
              <Typography variant="body1" fontWeight={600}>{record.durationHours || 0}h</Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Cost</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(getCostAmount(record.cost))}</Typography>
            </Box>
          </Box>
        </Paper>

        {record.status === 'Completed' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            Training completed successfully — certificate on file
          </Alert>
        )}
        {record.status === 'InProgress' && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<School />}>
            Training in progress — {record.durationHours}h programme ({progress}% complete)
          </Alert>
        )}
        {record.status === 'NotCompleted' && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<Cancel />}>
            Training not completed — may require SETA reporting adjustment
          </Alert>
        )}
        {record.status === 'Cancelled' && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<Cancel />}>
            Training was cancelled — no SETA credit claimable
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Employee Name', value: getEmployeeName(employeeMap, record.employee?.id), icon: <Person fontSize="small" /> },
            { label: 'Employee Number', value: getEmployeeField(employeeMap, record.employee?.id, 'employeeNumber') },
            { label: 'Department', value: getEmployeeField(employeeMap, record.employee?.id, 'department') },
            { label: 'Intervention Type', value: record.interventionType, chip: true, chipColor: interventionColor },
            { label: 'Funding Source', value: record.fundingSource || '—' },
            { label: 'Start Date', value: record.startDate || '—' },
            { label: 'End Date', value: record.endDate || '—' },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {(f as { icon?: React.ReactNode }).icon}{(f as { label?: string }).label}
                </Typography>
                {(f as { chip?: boolean; chipColor?: string }).chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" sx={{ bgcolor: (f as { chipColor?: string }).chipColor, color: 'white' }} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Training Lifecycle</Typography>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                <Step completed={TRAINING_STAGES.indexOf(record.status) > 0 || record.status === 'Completed'}>
                  <StepLabel>Planned</StepLabel>
                </Step>
                <Step completed={TRAINING_STAGES.indexOf(record.status) > 1 || record.status === 'Completed'}>
                  <StepLabel>In Progress</StepLabel>
                </Step>
                <Step completed={record.status === 'Completed'}>
                  <StepLabel>Completed</StepLabel>
                </Step>
              </Stepper>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Progress</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 1 }} color={progressColor as ProgressColor} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ minWidth: 48, textAlign: 'right' }}>{progress}%</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {record.status === 'Completed' ? 'Training complete' : record.status === 'InProgress' ? `${daysSinceStart} of ${totalDays} days elapsed` : 'Not started'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Training Stages</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Click a stage to advance or toggle its status</Typography>
          <Grid container spacing={2}>
            {TRAINING_STAGE_DEFS.map((stage) => {
              const done = stageStatus[stage.id];
              const canToggle =
                (stage.id === 'inProgress' && record.status === 'Planned') ||
                (stage.id === 'completed' && record.status === 'InProgress') ||
                stage.id === 'reported';
              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stage.id}>
                  <Card
                    variant="outlined"
                    onClick={() => canToggle && handleStageToggle(stage.id)}
                    sx={{
                      cursor: canToggle ? 'pointer' : 'default',
                      height: '100%',
                      borderLeft: '4px solid',
                      borderColor: done ? 'success.main' : 'grey.300',
                      opacity: stage.id === 'reported' && !done ? 0.7 : 1,
                      '&:hover': canToggle ? { boxShadow: 1 } : {},
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: done ? 'success.main' : 'grey.300' }}>
                            {stage.icon}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{stage.label}</Typography>
                        </Box>
                        {done ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <HourglassEmpty color="disabled" fontSize="small" />
                        )}
                      </Box>
                      <Typography variant="caption" color={done ? 'text.secondary' : 'transparent'} sx={{ mt: 0.5, display: 'block', ml: 5, userSelect: 'none' }}>
                        {done ? stage.desc : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        <ProcessActionsGrouped
          groups={[
            {
              title: 'Training Progress',
              actions: [
                {
                  label: 'Start Training',
                  description: 'Mark the training as started. Sets start date to today and moves to In Progress status.',
                  icon: <TrendingUp />,
                  color: 'info',
                  condition: record.status === 'Planned',
                  onClick: () => handleAction({ status: 'InProgress', startDate: record.startDate || new Date().toISOString().split('T')[0] }),
                  loading: isLoading,
                },
                {
                  label: 'Complete Training',
                  description: 'Mark training as successfully completed. Records end date and enables SETA reporting.',
                  icon: <CheckCircle />,
                  color: 'success',
                  condition: record.status === 'InProgress',
                  onClick: () => handleAction({ status: 'Completed', endDate: record.endDate || new Date().toISOString().split('T')[0] }),
                  loading: isLoading,
                },
                {
                  label: 'Mark Not Completed',
                  description: 'Training was not completed as planned. Records partial completion for audit and SETA reporting.',
                  icon: <Cancel />,
                  color: 'warning',
                  variant: 'outlined',
                  condition: record.status === 'InProgress',
                  onClick: () => handleAction({ status: 'NotCompleted' }),
                  loading: isLoading,
                },
                {
                  label: 'Cancel Training',
                  description: 'Cancel this training record. No SETA credit will be claimed. Cannot be undone.',
                  icon: <Cancel />,
                  color: 'error',
                  variant: 'outlined',
                  condition: record.status === 'Planned' || record.status === 'InProgress',
                  onClick: () => handleAction({ status: 'Cancelled' }),
                  loading: isLoading,
                },
              ],
            },
            {
              title: 'Reporting',
              actions: [
                {
                  label: 'Report to SETA',
                  description: 'Submit training completion to SETA for B-BBEE credit claim. Required for completed trainings.',
                  icon: <Assignment />,
                  color: 'success',
                  condition: record.status === 'Completed' && !record.setaReported,
                  onClick: () => handleAction({ setaReported: true }),
                  loading: isLoading,
                },
                {
                  label: 'Reported to SETA',
                  description: 'SETA reporting has been completed and recorded for this training.',
                  icon: <CheckCircle />,
                  color: 'inherit',
                  variant: 'outlined',
                  condition: record.status === 'Completed' && record.setaReported,
                  disabled: true,
                },
              ],
            },
          ]}
          isLoading={isLoading}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            {record.description && (
              <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Description / Learning Outcomes</Typography>
                <Typography variant="body2">{record.description}</Typography>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Financial Summary</Typography>
              <Box sx={{ p: 2, bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Cost</Typography>
                <Typography variant="h5" fontWeight={700}>{formatCurrency(getCostAmount(record.cost))}</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Cost per Hour</Typography>
                <Typography variant="body1" fontWeight={600}>R {costPerHour.toFixed(2)}</Typography>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Compliance Status</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle fontSize="small" color={record.status === 'Completed' ? 'success' : 'disabled'} />
                  <Typography variant="body2">{record.status === 'Completed' ? 'Certificate Issued' : 'Pending Certification'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment fontSize="small" color={record.ofocode?.id || record.ofocCode ? 'success' : 'disabled'} />
                  <Typography variant="body2">{record.ofocode?.id || record.ofocCode ? 'OFO Code Assigned' : 'OFO Code Missing'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt fontSize="small" color={record.fundingSource ? 'success' : 'disabled'} />
                  <Typography variant="body2">{record.fundingSource || 'Funding Source Unknown'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment fontSize="small" color={record.setaReported ? 'success' : 'disabled'} />
                  <Typography variant="body2">{record.setaReported ? 'SETA Reported' : 'Not Yet Reported'}</Typography>
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button component={Link} to="/contracts" variant="outlined" size="small" startIcon={<Receipt />} fullWidth>
                Learnership Contracts
              </Button>
              <Button component={Link} to="/trainingProviders" variant="outlined" size="small" startIcon={<Business />} fullWidth>
                View Provider
              </Button>
              <Button component={Link} to={record.employee?.id ? `/employees/${record.employee.id}/show` : '/employees'} variant="outlined" size="small" startIcon={<Person />} fullWidth>
                View Employee
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
};

export const TrainingShow = () => (
  <Show>
    <TrainingShowContent />
  </Show>
);

export const TrainingCreate = () => {
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('employeeId') || '';

  return (
    <Create>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 2 }}>
          Training records contribute to B-BBEE Skills Development score
        </Alert>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          <ReferenceInput source="employee.id" reference="employees" fullWidth defaultValue={employeeId}>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>
          <TextInput source="department" label="Department" fullWidth />
        <TextInput source="courseName" label="Course Name" fullWidth />
        <SelectInput source="interventionType" choices={INTERVENTION_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <TextInput source="provider" label="Provider" fullWidth />
        <TextInput source="startDate" label="Start Date" type="date" fullWidth />
        <TextInput source="endDate" label="End Date" type="date" fullWidth />
        <NumberInput source="durationHours" label="Duration (hours)" fullWidth />
        <NumberInput source="cost" label="Cost (ZAR)" fullWidth />
        <SelectInput source="fundingSource" choices={[
          { id: 'Company Funded', name: 'Company Funded' },
          { id: 'SETA Grant', name: 'SETA Grant' },
          { id: 'Bursary', name: 'Bursary' },
          { id: 'Learnership', name: 'Learnership' },
        ]} fullWidth />
        <SelectInput source="status" choices={TRAINING_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <OfoCodeAutocomplete source="ofocCode" label="OFO Code" />
      </Box>
      <TextInput source="description" multiline fullWidth label="Description" rows={2} />
    </SimpleForm>
  </Create>
  );
};

export const TrainingEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <ReferenceInput source="employee.id" reference="employees" fullWidth>
          <SelectInput optionText="name" label="Employee" />
        </ReferenceInput>
        <TextInput source="courseName" label="Course Name" fullWidth />
        <TextInput source="provider" fullWidth />
        <SelectInput source="interventionType" choices={INTERVENTION_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <SelectInput source="status" choices={TRAINING_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <DateField source="startDate" />
        <DateField source="endDate" />
        <NumberField source="cost" label="Cost (ZAR)" />
        <OfoCodeAutocomplete source="ofocCode" label="OFO Code" />
      </Box>
      <TextInput source="description" multiline fullWidth />
    </SimpleForm>
  </Edit>
);